import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { SidebarErrorState } from '@/components/layout/sidebar/SidebarErrorState'
import { fetchWithAuth } from '@/lib/api/core'
import { testIds } from '@/lib/test-ids'

export function DriveImagePreview({
  fileId,
  name,
  organizationId,
}: {
  fileId: string
  name?: string
  organizationId?: string
}) {
  const [src, setSrc] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    let objectUrl: string | null = null
    const load = async () => {
      setLoading(true)
      setError(null)
      setSrc(null)
      try {
        const params = organizationId
          ? `?organizationId=${encodeURIComponent(organizationId)}`
          : ''
        const res = await fetchWithAuth(
          `/api/google-drive/files/${encodeURIComponent(fileId)}/media${params}`,
        )
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { error?: string }
          throw new Error(
            typeof body.error === 'string' ? body.error : `HTTP ${res.status}`,
          )
        }
        const blob = await res.blob()
        if (cancelled) return
        objectUrl = URL.createObjectURL(blob)
        setSrc(objectUrl)
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load image')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [fileId, organizationId])

  if (loading) {
    return (
      <div
        className="flex h-full items-center gap-2 px-4 py-4 text-sm text-muted-foreground"
        data-testid={testIds.driveFolderView.previewImage}
      >
        <Loader2 className="h-4 w-4 animate-spin shrink-0" />
        Loading image
      </div>
    )
  }

  if (error || !src) {
    return (
      <div className="h-full overflow-y-auto" data-testid={testIds.driveFolderView.previewImage}>
        <SidebarErrorState message={error ?? 'Failed to load image'} />
      </div>
    )
  }

  return (
    <div
      className="flex h-full min-h-0 items-center justify-center overflow-auto bg-muted/20 p-4"
      data-testid={testIds.driveFolderView.previewImage}
    >
      <img
        src={src}
        alt={name?.trim() || 'Drive image'}
        className="max-h-full max-w-full object-contain"
      />
    </div>
  )
}
